// The Aid Extension Flowchart, started from an Aid Planner matter.
//
// On its own the flowchart asks staff what aid is in place, what the matter is listed for and
// which fee schedule applies. An Aid Planner matter already knows all three from its letters,
// so the flowchart opens inside the matter with those answers filled in, and with what the
// matter's own reminders say about an extension.

import { normaliseFeeTable } from "./approvalLetterGrants.js";

// The flowchart's fee schedules: summary (Table A), bail (Tables B-D), indictable (Table F and
// the higher-court tables) and family.
const BAIL_TABLES = new Set(["B", "C", "D"]);
const SUMMARY_TABLES = new Set(["A"]);

function scheduleForText(text = "") {
  const value = String(text).toLowerCase();
  if (/\bbail\b/.test(value)) return "bail";
  if (/family law|child support/.test(value)) return "family";
  if (/indictable|committal|county|supreme|appeal|cmia|high court|serious offender/.test(value)) return "indictable";
  if (/summary/.test(value)) return "summary";
  return "";
}

// Which of the flowchart's fee schedules the matter's grant falls under.
export function flowchartScheduleForMatter(planner = {}) {
  if (planner.matter?.matterType === "Family") return "family";
  const grants = planner.aid?.approvedGrants || [];
  const table = normaliseFeeTable(grants[grants.length - 1]?.feeTable || "");
  if (table) {
    if (BAIL_TABLES.has(table)) return "bail";
    if (SUMMARY_TABLES.has(table)) return "summary";
    if (/^[E-L]|^[TZ]$/.test(table)) return "indictable";
  }
  const aidTypes = [...(planner.aid?.aidTypeList || []), planner.aid?.aidType || ""].join(" ");
  return scheduleForText(aidTypes) || scheduleForText(planner.matter?.court) || "summary";
}

// The flowchart's first question: what aid is in place.
export function aidInPlaceForMatter(planner = {}) {
  const funding = planner.funding || {};
  const status = String(planner.aid?.applicationStatus || "");
  const granted = funding.grantInPlace || /granted|approved/i.test(status);
  if (granted) {
    const extended = Number(planner.aid?.extensionNumber) > 0 || (planner.aid?.extensions || []).length > 0;
    return extended ? "Further assistance / extension already approved" : "Initial grant of aid";
  }
  // "Pending" is a new matter's default, so only a lodged application counts.
  if (funding.aidApplied || planner.application?.lodgedInAtlas || /submitted|requisition/i.test(status)) return "Aid pending";
  return "No aid currently in place";
}

// Every answer the flowchart can take from the matter. appearanceOptions are the flowchart's
// listing choices; a listing not among them is entered as "Other".
export function flowchartStartFromMatter(planner = {}, appearanceOptions = []) {
  const listing = String(planner.matter?.appearanceType || "").trim();
  const known = appearanceOptions.find((option) => option.toLowerCase() === listing.toLowerCase());
  return {
    matterType: flowchartScheduleForMatter(planner),
    request: {
      aidInPlace: aidInPlaceForMatter(planner),
      aidInPlaceOther: "",
      requestType: listing ? "appearance" : "",
      appearanceType: listing ? known || "Other" : "",
      appearanceOther: listing && !known ? listing : "",
      disbursementType: "",
      disbursementOther: "",
    },
  };
}
